var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import React from "react";
import { Box, ChakraButton, Icon } from "../../ui";
var BTN_ICON_SIZE = "18px";
/**
 * Button component is used to trigger an action or event, such as submitting a form, opening a Dialog, canceling an action, or performing a delete operation.
 * Button composes and `Chakra's Button component` and `Chakra's Box component`, so you can pass all its props.
 *
 * See more on  **Chakra API** or directly in [Chakra's documentation](https://chakra-ui.com/docs/components/button/usage)
 *
 * ```tsx
 * import { Button, ButtonProps } from '@springcare/sh-component-library'
 * <Button>Label</Button>
 * ```
 *
 * ### Verdant API (Props)**
 * [Verdant's button specification](https://www.figma.com/file/ociQj4dnWL3DNErHV3rtkl/Button)
 *
 * #### Variant
 * - `high-emphasis`
 * - `medium-emphasis`
 * - `low-emphasis`
 * - `no-emphasis`
 *
 * #### Size
 * - `sm`
 * - `md`
 * - `lg`
 *
 * #### State
 * - Static (default)
 * - isSelected (prop)
 * - isDisabled (prop)
 * - Hover/Active
 *
 * #### Icon
 * - Off (icon undefined)
 * - On (icon defined, see [SVG Icon Reference](https://fantastic-broccoli-de639a7d.pages.github.io/?path=/docs/foundations-icons-icon-gallery--page))
 *
 * <br>
 * <details>
 * <summary>**Chakra API**</summary>
 * <br>
 * Button composes and `Chakra's Button component` and `Chakra's Box props or Style Props`
 * - [Chakra Button Props](https://chakra-ui.com/docs/components/button/props)
 * - [Chakra Style Props](https://chakra-ui.com/docs/styled-system/style-props)
 *
 * ### Differences with Chakra
 * Removing these props:
 * - "spinner"
 * - "rightIcon"
 * - "leftIcon"
 * - "iconSpacing"
 * - "spinnerPlacement"
 * - "loadingText"
 * </details>
 *
 *
 * @param {ButtonProps} props Verdant Button Props, override of Chakra's button props
 * @returns {React.ReactElement} Button
 * @author Alejandro De la Cruz Tarin <adelacruz.ctr@springhealth.com>
 * @see _buttonTheme button theme definition using chakra
 * @version 1.0.0
 */
export var Button = React.forwardRef(function (_a, ref) {
    var icon = _a.icon, children = _a.children, _b = _a.variant, variant = _b === void 0 ? "high-emphasis" : _b, _c = _a.size, size = _c === void 0 ? "md" : _c, buttonProps = __rest(_a, ["icon", "children", "variant", "size"]);
    return (_jsx(ChakraButton, __assign({ iconSpacing: "v-4", variant: "v-".concat(variant), size: "v-".concat(size), ref: ref, leftIcon: 
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        icon ? _jsx(Icon, { boxSize: BTN_ICON_SIZE, as: icon }) : undefined }, buttonProps, { children: _jsx(Box, __assign({ as: "span", px: "v-4" }, { children: children })) })));
});
